import type { GridSnapshot, LiveBuildRule, NodeKind, NodeState, RecipeDef } from "@reagent/sim";
import { CARDS } from "@reagent/content";
import { reagentLook } from "../../reagents";

/**
 * Spec 0067 T8: the costs the Play-phase tray and the inspector buttons show
 * BEFORE the player acts. Pure and snapshot-only, so a label can never disagree
 * with the engine: every kW and ₵ figure is read off `snapshot.liveBuild`,
 * never re-typed here.
 *
 * Outside Play (Setup) every labour and fee is 0 — Setup building is free of
 * kW, and a Setup move costs nothing — so one set of helpers serves both.
 */

/** The rule in force, or null in Setup / a stage with no `liveBuild`. */
export function playRule(snapshot: GridSnapshot): LiveBuildRule | null {
  return snapshot.phase === "play" ? snapshot.liveBuild : null;
}

export function creditsLeft(snapshot: GridSnapshot): number {
  return snapshot.setupBudgetCredits - snapshot.setupSpentCredits;
}

export function kwLeft(snapshot: GridSnapshot): number {
  return snapshot.energyBudgetKw - snapshot.energySpentKw;
}

export type BuildAction = "install" | "dismantle" | "move" | "rebind";

/** kW labour a build action charges right now. */
export function labourKw(snapshot: GridSnapshot, action: BuildAction): number {
  const rule = playRule(snapshot);
  if (!rule) return 0;
  switch (action) {
    case "install":
    case "rebind": // Spec 0068 OQ6: a rebind reuses the install labour, no new rule field.
      return rule.installLabourKw;
    case "dismantle":
      return rule.dismantleLabourKw;
    case "move":
      return rule.moveLabourKw;
  }
}

/** ₵ a move charges right now (0 in Setup). */
export function moveCredits(snapshot: GridSnapshot): number {
  return playRule(snapshot)?.moveCostCredits ?? 0;
}

/** Why a priced action is out of reach, or null when it can be taken. */
export type Shortfall = "credits" | "power" | null;

/** Checks ₵ before kW, the order the engine refuses in. */
export function shortfall(snapshot: GridSnapshot, credits: number, kw: number): Shortfall {
  if (credits > creditsLeft(snapshot)) return "credits";
  if (kw > kwLeft(snapshot)) return "power";
  return null;
}

/** "10 ₵ · 1 kW" — a zero part is still said, so "0 kW" reads as free labour, not a missing figure. */
export function costLabel(credits: number, kw: number): string {
  return `${credits} ₵ · ${kw} kW`;
}

export function shortfallLabel(reason: Shortfall): string | null {
  if (reason === "credits") return "not enough ₵";
  if (reason === "power") return "not enough kW";
  return null;
}

/**
 * Whether the tray lists an offer. A facility mitigation is a Setup-only
 * install (spec 0067 "Install in Play"), so it is hidden, not greyed, in Play.
 */
export function offerVisible(snapshot: GridSnapshot, isFacility: boolean): boolean {
  return !(isFacility && snapshot.phase === "play");
}

/**
 * What a Play dismantle pays. A clean unit's ladder refund is cut by
 * `playRefundPct`; a contaminated one pays the unchanged disposal fee (a
 * negative number), mirroring `engine/setup.ts`'s `refund`.
 */
export function playDismantleRefund(ladderRefund: number, contaminated: boolean, rule: LiveBuildRule | null): number {
  return rule && !contaminated ? Math.floor((ladderRefund * rule.playRefundPct) / 100) : ladderRefund;
}

export function soldBackLabel(count: number): string {
  if (count === 0) return "No pipes are attached, so nothing is sold back.";
  return `${count} attached pipe${count === 1 ? "" : "s"} will be sold back at full price — you lay new ones afterwards.`;
}

/**
 * Spec 0068: a recipe named by what it makes. Two recipes can share inputs, so
 * the output species is what tells a player which line they are choosing.
 */
export function recipeOutputLabel(recipe: RecipeDef): string {
  return Object.keys(recipe.outputs)
    .map((species) => reagentLook(species).label)
    .join(" + ");
}

/** Recipes a node of `kind` may be bound to, in snapshot order. */
export function recipesForKind(snapshot: GridSnapshot, kind: NodeKind): readonly RecipeDef[] {
  return snapshot.recipes.filter((r) => r.nodeKinds.includes(kind));
}

/**
 * Whether the engine's `bindRecipe` would accept this node's kind: some stage
 * recipe runs on it, and it has not exploded (spec 0068 OQ9).
 */
export function canRebind(snapshot: GridSnapshot, node: NodeState): boolean {
  return !node.isDestroyed && recipesForKind(snapshot, node.kind).length > 0;
}

/** Whether an offer's card deploys a reactor, the one kind the install picker serves. */
export function isReactorOffer(cardId: string): boolean {
  return CARDS[cardId]?.deploysNode === "reactor";
}

function outputNames(snapshot: GridSnapshot, recipeIds: readonly string[]): string | null {
  const names: string[] = [];
  for (const id of recipeIds) {
    const recipe = snapshot.recipes.find((r) => r.id === id);
    if (!recipe) return null;
    names.push(recipeOutputLabel(recipe));
  }
  return names.join(", ");
}

/** Output species of the recipe a card carries, or null for an unbound card. */
export function boundCardRecipeLabel(snapshot: GridSnapshot, cardId: string): string | null {
  const ids = CARDS[cardId]?.deploysNodeConfig?.recipeIds;
  return ids && ids.length > 0 ? outputNames(snapshot, ids) : null;
}

/** What a node runs, by output species; "any recipe" when unbound. */
export function nodeBindingLabel(snapshot: GridSnapshot, node: NodeState): string {
  return node.recipeIds === null ? "any recipe" : (outputNames(snapshot, node.recipeIds) ?? "any recipe");
}
